import { useState } from 'react';
import { PageCard } from '../../../Compounds/CardWithTitle';
import { Wizard, WizardConfiguration } from '../../../Compounds/Wizards/Wizard';
import { AssetSelectionStep } from '../../../Compounds/AssetConverter/AssetSelectionStep';
import { FieldMappingStep } from '../../../Compounds/AssetConverter/FieldMappingStep';
import { MigrationReviewStep } from '../../../Compounds/AssetConverter/MigrationReviewStep';
import { DEFAULT_ASSET_FIELD_MAPPINGS } from '../../../Compounds/AssetConverter/assetFieldMappingDefaults';
import {
  AssetFieldMapping,
  AssetMigrationValidationResponse,
  validateAssetMigration
} from '../../../../store/OutsideInventory';
import { useSnackbar } from '../../../../Context/SnackbarContext';

const INITIAL_VALIDATION_RESPONSE: AssetMigrationValidationResponse = {
  valid_count: 0,
  error_count: 0,
  errors: [],
  valid_asset_ids: [],
  preview: []
};

const STEP_TITLES: Record<number, string> = {
  1: 'Asset Selection',
  2: 'Field Mapping',
  3: 'Review Assets to Insert'
};

export const AssetConverterTab = () => {
  const { setSnack } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [fieldMappings, setFieldMappings] = useState<AssetFieldMapping[]>([...DEFAULT_ASSET_FIELD_MAPPINGS]);
  const [validationResponse, setValidationResponse] =
    useState<AssetMigrationValidationResponse>(INITIAL_VALIDATION_RESPONSE);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [hideCtas, setHideCtas] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [wizardKey, setWizardKey] = useState(0);

  const resetWizard = () => {
    setFile(null);
    setFieldMappings([...DEFAULT_ASSET_FIELD_MAPPINGS]);
    setValidationResponse(INITIAL_VALIDATION_RESPONSE);
    setHideCtas(false);
    setValidating(false);
    setValidationError(null);
    setCurrentStep(1);
    setConfirmModalOpen(false);
    setWizardKey((prev) => prev + 1);
  };

  const handleRemoveMapping = (portalFieldKey: string) => {
    setFieldMappings((prev) => prev.filter((m) => m.portalFieldKey !== portalFieldKey));
  };

  const handleValidate = () => {
    if (!file) return;
    setValidationError(null);
    setValidating(true);
    validateAssetMigration(file, fieldMappings).then((result) => {
      setValidating(false);
      if (result.error) {
        setValidationError(result.error.message);
        setSnack({ message: result.error.message, type: 'error', open: true });
      }
      if (result.data) {
        setValidationResponse(result.data);
      }
    });
  };

  const handleRetryValidation = () => {
    setValidationError(null);
    handleValidate();
  };

  const handleInsertClick = () => {
    setConfirmModalOpen(true);
  };

  const wizardConfigurations: WizardConfiguration[] = [
    {
      step: 1,
      childComponent: 0,
      headerCopy: 'Asset Selection',
      progressBarCopy: 'Asset Selection',
      nextStep: 2,
      nextBtnCopy: 'Next',
      prevStep: null,
      canMoveNextStep: !!file,
      canMovePrevStep: false
    },
    {
      step: 2,
      childComponent: 1,
      headerCopy: 'Field Mapping',
      progressBarCopy: 'Field Mapping',
      nextStep: 3,
      nextBtnCopy: 'Next',
      nextBtnClickHandler: handleValidate,
      prevStep: 1,
      prevBtnCopy: 'Cancel',
      canMoveNextStep: fieldMappings.length > 0,
      canMovePrevStep: true
    },
    {
      step: 3,
      childComponent: 2,
      headerCopy: 'Review Assets to Insert',
      progressBarCopy: 'Review',
      nextStep: validationResponse.valid_count > 0 && !hideCtas ? 3 : null,
      nextBtnCopy: validationResponse.valid_count > 0 && !hideCtas ? 'Insert' : 'Migrate Additional Assets',
      nextBtnClickHandler: handleInsertClick,
      showExitBtn: !hideCtas,
      exitBtnCopy: 'Cancel',
      handleExitWizard: resetWizard,
      prevStep: null,
      canMoveNextStep: (validationResponse.valid_count > 0 && !hideCtas) || hideCtas,
      canMovePrevStep: false
    }
  ];

  return (
    <PageCard
      title={STEP_TITLES[currentStep]}
      dataCy="asset-converter-tab"
      enableCollapse={false}
      childrenWrapperClass="min-h-80 max-w-[90vw] min-w-full">
      <Wizard
        key={wizardKey}
        configurations={wizardConfigurations}
        dataLoading={validating}
        hideHeader
        onStepChange={setCurrentStep}>
        {[
          <AssetSelectionStep
            key="step-1"
            file={file}
            onFileSelect={setFile}
          />,
          <FieldMappingStep
            key="step-2"
            mappings={fieldMappings}
            onRemoveMapping={handleRemoveMapping}
          />,
          <MigrationReviewStep
            key="step-3"
            validationResponse={validationResponse}
            fieldMappings={fieldMappings}
            validating={validating}
            confirmModalOpen={confirmModalOpen}
            onConfirmModalClose={() => setConfirmModalOpen(false)}
            onReset={resetWizard}
            onHideCtas={setHideCtas}
            validationError={validationError}
            onRetryValidation={handleRetryValidation}
          />
        ]}
      </Wizard>
    </PageCard>
  );
};
